import { useCallback, useEffect, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { exportSignaturePng, SIGNATURE_PAD_PEN } from './signature-png-export';
import { isDrawnSignature } from './signature-strokes';

/** i18n keys of the errors shown under the pad. */
export const SIGNATURE_PAD_ERRORS = {
  EMPTY: 'signature-form.errors.empty',
  EXPORT_FAILED: 'signature-form.errors.export-failed',
} as const;

export type SignaturePadError =
  (typeof SIGNATURE_PAD_ERRORS)[keyof typeof SIGNATURE_PAD_ERRORS];

/**
 * Device pixels per CSS pixel, at least 1, so strokes stay sharp on high
 * density screens.
 *
 * @returns The canvas backing-store ratio.
 */
function getPixelRatio(): number {
  return Math.max(window.devicePixelRatio || 1, 1);
}

/**
 * Sizes the canvas backing store to its displayed size times the pixel ratio
 * and scales its context so the pad draws in CSS pixels. Resetting the size
 * clears the canvas.
 *
 * @param canvas - The pad's canvas.
 */
function fitCanvasToDisplay(canvas: HTMLCanvasElement): void {
  const ratio = getPixelRatio();
  canvas.width = canvas.offsetWidth * ratio;
  canvas.height = canvas.offsetHeight * ratio;
  canvas.getContext('2d')?.scale(ratio, ratio);
}

/**
 * Re-fits the canvas after its displayed size changed (screen rotation,
 * window resize) and redraws the strokes already on the pad.
 *
 * @param params.canvas - The pad's canvas.
 * @param params.pad - The pad drawing on it.
 */
function refitPad({
  canvas,
  pad,
}: {
  canvas: HTMLCanvasElement;
  pad: SignaturePad;
}): void {
  const ratio = getPixelRatio();
  const isFitted =
    canvas.width === canvas.offsetWidth * ratio &&
    canvas.height === canvas.offsetHeight * ratio;
  if (isFitted) {
    return;
  }
  const strokes = pad.toData();
  fitCanvasToDisplay(canvas);
  pad.fromData(strokes);
}

/**
 * Drives the signature pad of the signature dialog: canvas setup at the
 * device's pixel ratio, refit on rotation, clearing, and validation.
 *
 * Validation rule: a pad that holds no actual signature (empty, a dot, a slip)
 * keeps the existing signature when there is one and closes; when there is
 * none yet it is refused with {@link SIGNATURE_PAD_ERRORS.EMPTY}.
 *
 * @param params.hasExistingSignature - Whether the doctor already has a
 *   signature (stored, legacy upload included, or drawn in this form).
 * @param params.onSave - Receives the exported PNG of a drawn signature.
 * @param params.onOpenChange - Closes the dialog once validated.
 * @returns The canvas ref callback, `clear`, `validate` and the current error.
 */
export function useSignaturePad({
  hasExistingSignature,
  onSave,
  onOpenChange,
}: {
  hasExistingSignature: boolean;
  onSave: (file: File) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const padRef = useRef<SignaturePad | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [error, setError] = useState<SignaturePadError | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const setCanvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    padRef.current?.off();
    padRef.current = null;
    canvasRef.current = canvas;
    if (!canvas) {
      return;
    }
    fitCanvasToDisplay(canvas);
    padRef.current = new SignaturePad(canvas, SIGNATURE_PAD_PEN);
  }, []);

  // Refit once the canvas has its new layout size (rotation, window resize).
  // Observing the canvas rather than `window.resize` matters: the resize event
  // can fire before media queries re-lay the dialog out, leaving a stale width.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(() => {
      const pad = padRef.current;
      if (pad) {
        refitPad({ canvas, pad });
      }
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  const clear = () => {
    padRef.current?.clear();
    setError(null);
  };

  const validate = async () => {
    const strokes = padRef.current?.toData() ?? [];
    if (!isDrawnSignature(strokes)) {
      if (hasExistingSignature) {
        onOpenChange(false);
        return;
      }
      setError(SIGNATURE_PAD_ERRORS.EMPTY);
      return;
    }

    const canvas = canvasRef.current;
    setIsSaving(true);
    try {
      const file = await exportSignaturePng({
        pointGroups: strokes,
        width: canvas?.offsetWidth ?? 0,
        height: canvas?.offsetHeight ?? 0,
      });
      onSave(file);
      onOpenChange(false);
    } catch (exportError) {
      console.error(exportError);
      setError(SIGNATURE_PAD_ERRORS.EXPORT_FAILED);
    } finally {
      setIsSaving(false);
    }
  };

  return { setCanvasRef, clear, validate, error, isSaving };
}
